import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import QRCode from 'react-native-qrcode-svg';

import { ThemedText } from '@/components/ThemedText';
import { useScale } from '@/hooks/useScale';
import { getTvUuid, setAccessToken } from '@/services/auth/authStorage';
import { startPairing, checkPairStatus } from '@/services/api/tvPairApi';
import { AxiosError } from 'axios';
import { colors } from '@/constants/colors';
import { PAIRING_LINK_BASE_URL } from '@/constants';
import DefaultBackground from '@/components/DefaultBackground';
import EmailPasswordLoginScreen from '@/components/EmailPasswordLoginScreen';
import Logo from '@/components/Logo';

const POLL_INTERVAL_MS = 5_000; // 5 seconds
const POLL_TIMEOUT_MS = 5 * 60_000; // 5 minutes

type PairState =
  | { step: 'loading' }
  | {
      step: 'showing_code';
      pairCode: string;
      isPolling: boolean;
    }
  | { step: 'error'; message: string };

type Mode = 'pair' | 'email';

export default function LoginScreen() {
  const scale = useScale();
  const styles = useStyles();
  const [state, setState] = useState<PairState>({ step: 'loading' });
  const [mode, setMode] = useState<Mode>('pair');

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expiryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (expiryTimeoutRef.current) {
      clearTimeout(expiryTimeoutRef.current);
      expiryTimeoutRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();

    const tvUuid = getTvUuid();

    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await checkPairStatus(tvUuid);

        if (response.data?.authenticated && response.data.access_token) {
          stopPolling();
          setAccessToken(response.data.access_token);
        }
      } catch (err) {
        stopPolling();
        const message =
          err instanceof Error ? err.message : 'Failed to check status';
        setState({ step: 'error', message });
      }
    }, POLL_INTERVAL_MS);

    // Stop polling after timeout, show fallback button
    pollTimeoutRef.current = setTimeout(() => {
      console.log(
        '[Auth] Poll timeout reached (5 min), stopping polling. Code still valid.',
      );
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setState((prev) =>
        prev.step === 'showing_code' ? { ...prev, isPolling: false } : prev,
      );
    }, POLL_TIMEOUT_MS);
  }, [stopPolling]);

  const handleStartPairing = async () => {
    stopPolling();
    setState({ step: 'loading' });
    try {
      const tvUuid = getTvUuid();
      console.log('[Auth] Starting pairing with TV UUID:', tvUuid);
      const response = await startPairing(tvUuid);

      if (!response.success || !response.data) {
        setState({
          step: 'error',
          message: response.error ?? 'Failed to start pairing',
        });
        return;
      }

      const { pair_code, expires_at } = response.data;
      console.log('[Auth] Received pair code, expires at:', expires_at);

      setState({
        step: 'showing_code',
        pairCode: pair_code,
        isPolling: true,
      });

      startPolling();

      const expiresAtMs = new Date(expires_at).getTime();
      const rawMsUntilExpiry = expiresAtMs - Date.now();
      const msUntilExpiry = Math.max(0, rawMsUntilExpiry);

      // Refresh 15 minutes early, but never more than half the code lifetime
      // So it's always the earlier of 15 minutes before expiry or halfway through the code's lifetime
      const refreshBufferMs = Math.min(15 * 60 * 1000, msUntilExpiry / 2);
      const msUntilRefresh = Math.max(0, msUntilExpiry - refreshBufferMs);
      if (msUntilRefresh > 0) {
        console.log(
          `[Auth] Code auto-regeneration scheduled in ${Math.round(msUntilRefresh / 1000 / 60)} minutes`,
        );
        expiryTimeoutRef.current = setTimeout(() => {
          console.log('[Auth] Pair code expired, auto-regenerating...');
          handleStartPairing();
        }, msUntilRefresh);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to connect to server';
      setState({ step: 'error', message });
      console.error('[Auth] Error starting pairing:', err);
      if (err instanceof AxiosError) {
        console.error('[Auth] Axios error details:', {
          status: err.response?.status,
          data: err.response?.data
            ? JSON.stringify(err.response.data, null, 2)
            : 'No response data',
        });
      }
    }
  };

  const handleCheckNow = () => {
    setState((prev) =>
      prev.step === 'showing_code' ? { ...prev, isPolling: true } : prev,
    );
    startPolling();
  };

  // Auto-start pairing on mount
  useEffect(() => {
    handleStartPairing();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSwitchToEmail = () => {
    stopPolling();
    setMode('email');
  };

  const handleBackToPair = () => {
    setMode('pair');
    if (state.step === 'showing_code') {
      setState((prev) =>
        prev.step === 'showing_code' ? { ...prev, isPolling: true } : prev,
      );
      startPolling();
    } else {
      handleStartPairing();
    }
  };

  if (mode === 'email') {
    return <EmailPasswordLoginScreen onBack={handleBackToPair} />;
  }

  return (
    <View style={styles.container}>
      <DefaultBackground />

      {/* Logo */}
      <View style={styles.logoContainer}>
        <Logo height={38 * scale} scaleProportionally />
      </View>

      {state.step === 'loading' && (
        <View style={styles.centeredContainer}>
          <ActivityIndicator size="large" color="#fff" />
          <ThemedText style={styles.description}>
            Getting pair code...
          </ThemedText>
        </View>
      )}

      {state.step === 'showing_code' && (
        <View style={styles.centeredContainer}>
          <ThemedText type="title" lightColor="#fff" darkColor="#fff">
            Pair with Your Phone
          </ThemedText>
          <ThemedText style={styles.description}>
            Scan this QR code with your phone's camera or the HiNote Life app,
            or enter the code manually under Profile → Settings → Pair TV.
          </ThemedText>
          <View style={styles.codeRow}>
            <View style={styles.qrContainer}>
              <QRCode
                value={`${PAIRING_LINK_BASE_URL}/profile/settings/tv-pair?code=${state.pairCode}`}
                size={160 * scale}
                backgroundColor="white"
                color={colors.brand.primary}
              />
            </View>
            <ThemedText style={styles.pairCode}>{state.pairCode}</ThemedText>
          </View>

          {state.isPolling ? (
            <View style={styles.pollingRow}>
              <ActivityIndicator
                size="small"
                color="rgba(255, 255, 255, 0.7)"
              />
              <ThemedText style={styles.pollingText}>
                Waiting for confirmation...
              </ThemedText>
            </View>
          ) : (
            <>
              <ThemedText style={styles.hintText}>
                Still waiting? Make sure you've confirmed in the HiNote Life
                app.
              </ThemedText>
              <Pressable
                style={({ focused }) => [
                  styles.primaryButton,
                  focused && styles.primaryButtonFocused,
                ]}
                onPress={handleCheckNow}
              >
                <ThemedText style={styles.primaryButtonText}>
                  Check Now
                </ThemedText>
              </Pressable>
            </>
          )}
        </View>
      )}

      {state.step === 'error' && (
        <View style={styles.centeredContainer}>
          <ThemedText type="title" lightColor="#fff" darkColor="#fff">
            Error
          </ThemedText>
          <ThemedText style={styles.errorText}>{state.message}</ThemedText>
          <Pressable
            style={({ focused }) => [
              styles.primaryButton,
              focused && styles.primaryButtonFocused,
            ]}
            onPress={handleStartPairing}
          >
            <ThemedText style={styles.primaryButtonText}>Try Again</ThemedText>
          </Pressable>
        </View>
      )}

      <View style={styles.footer}>
        <Pressable
          style={({ focused }) => [
            styles.emailLoginButton,
            focused && styles.emailLoginButtonFocused,
          ]}
          onPress={handleSwitchToEmail}
        >
          <ThemedText style={styles.emailLoginButtonText}>
            Login with email and password
          </ThemedText>
        </Pressable>

        <ThemedText style={styles.poweredByHint}>
          HiNote Life - Powered by fit52
        </ThemedText>
      </View>
    </View>
  );
}

function useStyles() {
  const scale = useScale();

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.brand.primary,
    },
    logoContainer: {
      alignItems: 'center',
      paddingTop: 32 * scale,
    },
    centeredContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24 * scale,
    },
    description: {
      marginTop: 12 * scale,
      fontSize: 16 * scale,
      color: 'rgba(255, 255, 255, 0.85)',
      textAlign: 'center',
      maxWidth: 500 * scale,
    },
    codeRow: {
      flexDirection: 'row',
      gap: 32 * scale,
      alignItems: 'center',
    },
    qrContainer: {
      marginTop: 24 * scale,
      padding: 16 * scale,
      backgroundColor: 'white',
      borderRadius: 12 * scale,
    },
    pairCode: {
      marginTop: 16 * scale,
      fontSize: 40 * scale,
      lineHeight: 48 * scale,
      fontWeight: '700',
      letterSpacing: 8 * scale,
      color: '#fff',
    },
    pollingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10 * scale,
      marginTop: 20 * scale,
    },
    pollingText: {
      fontSize: 16 * scale,
      color: 'rgba(255, 255, 255, 0.7)',
    },
    hintText: {
      marginTop: 20 * scale,
      fontSize: 14 * scale,
      color: 'rgba(255, 255, 255, 0.6)',
      textAlign: 'center',
    },
    poweredByHint: {
      fontSize: 14 * scale,
      color: 'rgba(255,255,255,0.8)',
      textAlign: 'center',
      marginTop: 12 * scale,
    },
    buttonRow: {
      flexDirection: 'row',
      gap: 16 * scale,
      alignItems: 'center',
      justifyContent: 'center',
    },
    primaryButton: {
      marginTop: 24 * scale,
      paddingVertical: 14 * scale,
      paddingHorizontal: 32 * scale,
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      borderRadius: 12 * scale,
      borderWidth: 2,
      borderColor: 'rgba(255, 255, 255, 0.4)',
    },
    primaryButtonFocused: {
      borderColor: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.35)',
    },
    primaryButtonText: {
      color: '#fff',
      fontSize: 18 * scale,
      fontWeight: '700',
    },
    errorText: {
      marginTop: 16 * scale,
      fontSize: 14 * scale,
      color: '#ffb3b3',
      textAlign: 'center',
    },
    footer: {
      alignItems: 'center',
      paddingBottom: 32 * scale,
    },
    emailLoginButton: {
      paddingVertical: 10 * scale,
      paddingHorizontal: 20 * scale,
      borderRadius: 8 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
    },
    emailLoginButtonFocused: {
      borderColor: 'rgba(255, 255, 255, 0.8)',
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
    emailLoginButtonText: {
      color: 'rgba(255, 255, 255, 0.75)',
      fontSize: 16 * scale,
      fontWeight: '600',
      textDecorationLine: 'underline',
    },
  });
}
